export type SharedStateDomain = 'user' | 'session' | 'config';

export interface OrdersHandoffRouteState {
  source?: string;
  action?: string;
  orderId?: string;
  businessContext?: Record<string, unknown>;
  previousUrl?: string;
}

export interface SharedStateUpdate<TState> {
  domain: SharedStateDomain;
  payload: TState;
  timestamp: number;
}

const eventBus = new EventTarget();

const eventNameFor = (domain: SharedStateDomain) => `shared-state:${domain}`;

export const publishSharedState = <TState>(
  domain: SharedStateDomain,
  payload: TState,
): SharedStateUpdate<TState> => {
  const update: SharedStateUpdate<TState> = {
    domain,
    payload,
    timestamp: Date.now(),
  };

  eventBus.dispatchEvent(new CustomEvent(eventNameFor(domain), { detail: update }));
  return update;
};

export const subscribeSharedState = <TState>(
  domain: SharedStateDomain,
  listener: (update: SharedStateUpdate<TState>) => void,
): (() => void) => {
  const eventHandler = (event: Event) => {
    const customEvent = event as CustomEvent<SharedStateUpdate<TState>>;
    listener(customEvent.detail);
  };

  eventBus.addEventListener(eventNameFor(domain), eventHandler);

  return () => {
    eventBus.removeEventListener(eventNameFor(domain), eventHandler);
  };
};

export interface StateAdapter<TState> {
  domain: SharedStateDomain;
  getSnapshot: () => TState;
  applyUpdate: (payload: TState) => void;
  publish: (payload: TState) => SharedStateUpdate<TState>;
  subscribe: (listener: (update: SharedStateUpdate<TState>) => void) => () => void;
  sync: () => SharedStateUpdate<TState>;
}

export const createStateAdapter = <TState>(
  domain: SharedStateDomain,
  getSnapshot: () => TState,
  applyUpdate: (payload: TState) => void,
): StateAdapter<TState> => ({
  domain,
  getSnapshot,
  applyUpdate,
  publish: (payload) => publishSharedState(domain, payload),
  subscribe: (listener) =>
    subscribeSharedState<TState>(domain, (update) => {
      applyUpdate(update.payload);
      listener(update);
    }),
  sync: () => publishSharedState(domain, getSnapshot()),
});

export const toOrdersHandoffRouteState = (
  state: OrdersHandoffRouteState,
): OrdersHandoffRouteState => ({
  source: state.source,
  action: state.action,
  orderId: state.orderId,
  businessContext: state.businessContext,
  previousUrl: state.previousUrl,
});

export const fromOrdersHandoffRouteState = (state: unknown): OrdersHandoffRouteState | null => {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const raw = state as Record<string, unknown>;

  return {
    source: typeof raw.source === 'string' ? raw.source : undefined,
    action: typeof raw.action === 'string' ? raw.action : undefined,
    orderId: typeof raw.orderId === 'string' ? raw.orderId : undefined,
    businessContext:
      raw.businessContext && typeof raw.businessContext === 'object'
        ? (raw.businessContext as Record<string, unknown>)
        : undefined,
    previousUrl: typeof raw.previousUrl === 'string' ? raw.previousUrl : undefined,
  };
};
